import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const STORAGE_KEY = 'solicitacao_tokens';

interface TokenStorage {
  [solicitacaoId: number]: string;
}

export function useSolicitacaoTokens() {
  const queryClient = useQueryClient();
  const [tokens, setTokens] = useState<TokenStorage>({});

  const loadTokensFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: TokenStorage = JSON.parse(stored);
        setTokens(parsed);
        return parsed;
      }
    } catch {
      // Ignora erros ao carregar tokens
    }
    return {};
  }, []);

  const saveToken = useCallback(
    (solicitacaoId: number, token: string) => {
      setTokens(prev => {
        const updated = { ...prev, [solicitacaoId]: token };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch {
          // Ignora erros ao salvar token
        }
        return updated;
      });
      // Invalida queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['minhas-solicitacoes'] });
    },
    [queryClient],
  );

  // Carrega tokens do localStorage ao montar
  useEffect(() => {
    loadTokensFromStorage();
  }, [loadTokensFromStorage]);

  // Listener para eventos de token recebido
  useEffect(() => {
    const handleTokenRecebido = (event: CustomEvent) => {
      const { solicitacao_id: solicitacaoId, token } = event.detail;
      saveToken(solicitacaoId, token);
    };

    window.addEventListener('token-recebido', handleTokenRecebido as EventListener);
    return () => {
      window.removeEventListener('token-recebido', handleTokenRecebido as EventListener);
    };
  }, [saveToken]);

  const getToken = useCallback(
    (solicitacaoId: number): string | undefined => {
      // Primeiro tenta do estado
      if (tokens[solicitacaoId]) {
        return tokens[solicitacaoId];
      }
      // Se não encontrou, busca no localStorage (sem atualizar estado durante render)
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: TokenStorage = JSON.parse(stored);
          if (parsed[solicitacaoId]) {
            // Atualiza o estado de forma assíncrona para evitar warning
            setTimeout(() => {
              setTokens(prev => {
                if (!prev[solicitacaoId]) {
                  return { ...prev, [solicitacaoId]: parsed[solicitacaoId] };
                }
                return prev;
              });
            }, 0);
            return parsed[solicitacaoId];
          }
        }
      } catch {
        // Ignora erros ao buscar token
      }
      return undefined;
    },
    [tokens],
  );

  const removeToken = useCallback((solicitacaoId: number) => {
    setTokens(prev => {
      const updated = { ...prev };
      delete updated[solicitacaoId];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // Ignora erros ao remover token
      }
      return updated;
    });
  }, []);

  const clearAllTokens = useCallback(() => {
    setTokens({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignora erros ao limpar tokens
    }
  }, []);

  return {
    tokens,
    getToken,
    saveToken,
    removeToken,
    clearAllTokens,
    loadTokensFromStorage,
  };
}
